import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AUTH } from "@/constants/testIds";
import {
  Briefcase,
  CrownSimple,
  Eye,
  EyeSlash,
  EnvelopeSimple,
  LockKey,
  PencilSimple,
  Sparkle,
  User,
} from "@phosphor-icons/react";

function formatError(detail) {
  if (!detail) return "Ocorreu um erro. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const { login, demoLogin, demoMode } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      const loggedUser = await login(email, password);
      navigate(loggedUser?.must_change_password ? "/trocar-senha" : "/dashboard");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = (role) => {
    demoLogin(role);
    navigate("/dashboard");
  };

  return (
    <div className="grid min-h-screen overflow-hidden bg-[#f5f7fb] lg:grid-cols-[1.02fr_0.98fr]">
      <section className="relative hidden flex-col justify-between overflow-hidden bg-[#2346ff] p-12 text-white lg:flex">
        <div className="absolute inset-0">
          <div className="absolute inset-8 rounded-[34px] border border-white/10" />
          <div className="absolute left-12 top-28 h-px w-44 bg-white/15" />
          <div className="absolute bottom-24 right-16 h-px w-64 bg-white/15" />
        </div>

        <div className="relative">
          <img
            src="/schumann-logo-blue-bg.svg"
            alt="Schumann"
            className="h-auto w-[220px]"
          />
          <p className="mt-6 text-xs uppercase tracking-[0.28em] text-white/62">
            Intranet corporativa
          </p>
        </div>

        <div className="relative max-w-[620px]">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/16 bg-white/10 px-3.5 py-2 text-sm text-white/88">
            <Sparkle className="h-4 w-4 text-[#ffd724]" weight="regular" />
            Central interna Schumann
          </div>

          <h1 className="font-heading text-[44px] font-bold leading-[1.08] tracking-normal xl:text-[58px]">
            Comunicação, documentos e áreas em um só lugar
          </h1>

          <p className="mt-6 max-w-lg text-base leading-7 text-white/72">
            Acesse novidades internas, procedimentos, treinamentos e informações autorizadas conforme o seu perfil.
          </p>
        </div>

        <p className="relative text-sm text-white/45">
          © {new Date().getFullYear()} Schumann. Todos os direitos reservados.
        </p>
      </section>

      <main className="flex min-h-screen items-center justify-center px-5 py-10">
        <div className="w-full max-w-[440px]">
          <div className="mb-7 flex justify-center lg:hidden">
            <img src="/schumann-logo.svg" alt="Schumann" className="h-12 w-auto" />
          </div>

          <div className="rounded-[26px] border border-slate-200/80 bg-white p-6 shadow-[0_18px_48px_rgba(15,23,42,0.08)] sm:p-8">
            <div className="mb-7">
              <h2 className="font-heading text-2xl font-bold text-slate-950">
                Bem-vindo
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                Acesso exclusivo para colaboradores autorizados pela base da empresa.
              </p>
            </div>

            <form data-testid={AUTH.loginForm} onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-semibold text-slate-700">
                  E-mail corporativo
                </Label>
                <div className="relative">
                  <EnvelopeSimple className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2346ff]" weight="regular" />
                  <Input
                    id="email"
                    type="email"
                    data-testid={AUTH.emailInput}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="nome@empresa.com.br"
                    autoComplete="email"
                    required
                    className="h-12 rounded-xl border-slate-200 bg-slate-50 pl-10 focus:border-[#2346ff] focus:ring-2 focus:ring-[#2346ff]/20"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-semibold text-slate-700">
                  Senha
                </Label>
                <div className="relative">
                  <LockKey className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2346ff]" weight="regular" />
                  <Input
                    id="password"
                    type={showPass ? "text" : "password"}
                    data-testid={AUTH.passwordInput}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    className="h-12 rounded-xl border-slate-200 bg-slate-50 pl-10 pr-11 focus:border-[#2346ff] focus:ring-2 focus:ring-[#2346ff]/20"
                  />
                  <button
                    type="button"
                    aria-label={showPass ? "Ocultar senha" : "Mostrar senha"}
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-lg text-slate-400 transition hover:bg-white hover:text-[#2346ff]"
                  >
                    {showPass ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <Alert variant="destructive" className="rounded-xl py-2">
                  <AlertDescription className="text-sm">{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                data-testid={AUTH.loginButton}
                disabled={loading}
                className="h-12 w-full rounded-xl bg-[#2346ff] font-semibold text-white shadow-[0_12px_26px_rgba(35,70,255,0.22)] transition hover:bg-[#1d3ace] disabled:opacity-70"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    Entrando...
                  </span>
                ) : "Entrar na Intranet"}
              </Button>
            </form>

            <div className="mt-4 flex justify-center">
              <RouterLink
                to="/primeiro-acesso"
                className="text-sm font-semibold text-[#2346ff] transition hover:text-[#1d3ace]"
              >
                Primeiro acesso ou criar senha
              </RouterLink>
            </div>

            {demoMode && (
              <div className="mt-5 border-t border-slate-200 pt-5">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-slate-800">Visualização de demonstração</p>
                    <p className="mt-0.5 text-xs text-slate-500">Escolha um perfil para testar a interface.</p>
                  </div>
                  <span className="rounded-full bg-[#eef1ff] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-[#2346ff]">
                    Local
                  </span>
                </div>

                <div className="grid gap-2">
                  {[
                    ["colaborador", "Entrar como Colaborador", User],
                    ["autor_area", "Entrar como Autor da área", PencilSimple],
                    ["gerente", "Entrar como Gerente", Briefcase],
                    ["administrador_total", "Entrar como Administrador", CrownSimple],
                  ].map(([role, label, Icon]) => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => handleDemoLogin(role)}
                      className="flex min-h-11 w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-left text-sm font-semibold text-slate-700 transition hover:border-[#2346ff]/40 hover:bg-[#f7f8ff] hover:text-[#2346ff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2346ff]/30"
                    >
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#eef1ff] text-[#2346ff]">
                        <Icon className="h-4 w-4" weight="regular" />
                      </span>
                      {label}
                    </button>
                  ))}
                </div>

                <p className="mt-3 text-[11px] leading-4 text-slate-400">
                  Apenas para avaliação visual. O ambiente definitivo exigirá autenticação segura no servidor.
                </p>
              </div>
            )}

            <div className="mt-5 rounded-2xl bg-[#fff8d7] px-4 py-3 text-xs leading-5 text-slate-700">
              Não há autocadastro. Se você não conseguir acessar, fale com o suporte interno de TI ou RH.
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
